import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, get, post, put } from "./client";

/** `/api/v1/admin/labour-allowances`: B15 supervision hours by valve make and
 * DN (`prv.web.api.labour_allowances`). Every write hands the whole list back,
 * and every write needs a reason, which the audit trail keeps. */

const BASE = "/api/v1/admin/labour-allowances";

export interface LabourAllowance {
  id: number;
  valve_make: string;
  make_label: string;
  dn_mm: number;
  /** "Bermad at DN150", "any make at DN150". */
  label: string;
  /** Hours as a person says them: "8", "6.5". */
  supervisor_hours: string;
  note: string | null;
  active: boolean;
  created_at: string | null;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface LabourAllowances {
  rows: LabourAllowance[];
  retired: LabourAllowance[];
  makes: { value: string; label: string }[];
  audit_url: string;
  saved: string | null;
}

export interface NewAllowance {
  valve_make: string;
  dn_mm: number;
  supervisor_hours: string;
  note: string | null;
  reason: string;
}

export const LABOUR_ALLOWANCES_KEY = ["admin", "labour-allowances"] as const;

export function useLabourAllowances() {
  return useQuery<LabourAllowances, ApiError>({ queryKey: LABOUR_ALLOWANCES_KEY, queryFn: () => get(BASE) });
}

function useAllowanceWrite<TVars>(write: (vars: TVars) => Promise<LabourAllowances>) {
  const client = useQueryClient();
  return useMutation<LabourAllowances, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (data) => {
      client.setQueryData(LABOUR_ALLOWANCES_KEY, data);
      void client.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}

export function useAddAllowance() {
  return useAllowanceWrite((body: NewAllowance) => post<LabourAllowances>(BASE, body));
}

export function useChangeAllowance() {
  return useAllowanceWrite((vars: { id: number; supervisor_hours: string; note: string | null; reason: string }) =>
    put<LabourAllowances>(`${BASE}/${vars.id}`, { supervisor_hours: vars.supervisor_hours, note: vars.note, reason: vars.reason }),
  );
}

export function useRetireAllowance() {
  return useAllowanceWrite((vars: { id: number; reason: string }) => post<LabourAllowances>(`${BASE}/${vars.id}/retire`, { reason: vars.reason }));
}
